import { GreetingBucket, getGreetingSelection } from '@/lib/greetingCache';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import {
  FileSpec,
  IStep,
  useAuth,
  useChatData,
  useChatInteract
} from '@chainlit/react-client';

import MaterialIcon from '@/components/MaterialIcon';
import ChatProfiles from '@/components/header/ChatProfiles';
import { GitHub } from '@/components/icons/Github';
import { appendGitHubRepositoryReference } from '@/lib/message';

import {
  AttachmentSource,
  IAttachment,
  attachmentsState,
  persistentCommandState,
  starterCardsState
} from 'state/chat';

import { Attachments } from './Attachments';
import Input, { InputMethods } from './Input';
import AttachmentMenu from './PlusMenu';
import SubmitButton from './SubmitButton';
import VoiceButton from './VoiceButton';

interface StarterCard {
  id: number;
  text: string;
  text_en?: string;
  icon: string;
  is_active: boolean;
  sort_order: number;
}

const COMMAND_ICON_MAP: Record<string, string> = {
  'notebook-pen': 'edit_note',
  layers: 'layers',
  'circle-help': 'help'
};

const ICON_MAP: Record<string, string> = {
  CalendarDays: 'calendar_month',
  MapPin: 'location_on',
  Info: 'info',
  Globe: 'public',
  MessageSquare: 'chat',
  HelpCircle: 'help',
  BookOpen: 'menu_book',
  GraduationCap: 'school',
  Building: 'apartment',
  Users: 'group',
  Clock: 'schedule',
  FileText: 'description',
  Search: 'search',
  Settings: 'settings',
  Star: 'star',
  Heart: 'favorite',
  Lightbulb: 'lightbulb',
  Target: 'target',
  Compass: 'explore',
  Award: 'award',
  Briefcase: 'work',
  Coffee: 'coffee'
};

const getMaterialIcon = (iconName: string): string => {
  return ICON_MAP[iconName] || 'chat';
};

const defaultCards: StarterCard[] = [
  {
    id: 1,
    text: 'Kada je kolokvijum iz predmeta?',
    text_en: 'When is the exam for this course?',
    icon: 'CalendarDays',
    is_active: true,
    sort_order: 0
  },
  {
    id: 2,
    text: 'Gde se nalazi amfiteatar 3?',
    text_en: 'Where is amphitheater 3 located?',
    icon: 'MapPin',
    is_active: true,
    sort_order: 1
  },
  {
    id: 3,
    text: 'Kako sve možete da mi pomognete?',
    text_en: 'What can you help me with?',
    icon: 'Info',
    is_active: true,
    sort_order: 2
  },
  {
    id: 4,
    text: 'Kako mogu da se prijavim za Erasmus razmenu?',
    text_en: 'How can I apply for an Erasmus exchange?',
    icon: 'Globe',
    is_active: true,
    sort_order: 3
  }
];

const STARTER_COPY_CORRECTIONS: Record<string, string> = {
  'Sa čim sve možete da mi pomognete?': 'Kako sve možete da mi pomognete?',
  'U čemu sve možete da mi pomognete?': 'Kako sve možete da mi pomognete?',
  'Kako mogu da odem na Erasmus razmenu?':
    'Kako mogu da se prijavim za Erasmus razmenu?',
  'When is the exam for the subject?': 'When is the exam for this course?',
  'How can I go on Erasmus exchange?':
    'How can I apply for an Erasmus exchange?',
  'When can i go to Erasmus exchange?':
    'How can I apply for an Erasmus exchange?'
};

const GITHUB_REPO_URL_RE =
  /^https:\/\/(www\.)?github\.com\/[\w.-]+\/[\w.-]+(\/[\w./-]*)?$/i;

interface Props {
  fileSpec: FileSpec;
  onFileUpload: (
    payload: File[],
    meta?: { source?: AttachmentSource }
  ) => void;
  onFileUploadError: (error: string) => void;
  autoScrollRef: MutableRefObject<boolean>;
  variant?: 'welcome' | 'chat';
}

export default function MessageComposer({
  fileSpec,
  onFileUpload,
  onFileUploadError,
  autoScrollRef,
  variant = 'chat'
}: Props) {
  const inputRef = useRef<InputMethods>(null);
  const [value, setValue] = useState('');
  const [selectedCommand, setSelectedCommand] = useRecoilState(
    persistentCommandState
  );
  const [attachments, setAttachments] = useRecoilState(attachmentsState);
  const [selectedRepoUrl, setSelectedRepoUrl] = useState<string | null>(null);
  const cards = useRecoilValue(starterCardsState);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const onRepoSelected = useCallback(
    (repoUrl: string) => {
      const trimmed = (repoUrl || '').trim();
      if (!GITHUB_REPO_URL_RE.test(trimmed)) {
        toast.error(
          t('chat.input.errors.githubUrl', 'Invalid GitHub repository URL')
        );
        return;
      }
      setSelectedRepoUrl(trimmed);
      inputRef.current?.focus();
    },
    [t]
  );

  const onRepoError = useCallback((error: string) => {
    console.error('GitHub repo error:', error);
    toast.error(error || t('chat.input.errors.github', 'GitHub repository error'));
  }, []);

  const { user } = useAuth();
  const { sendMessage, replyMessage } = useChatInteract();
  const { askUser, disabled: _disabled } = useChatData();

  const disabled = _disabled || !!attachments.find((a) => !a.uploaded);
  const isWelcome = variant === 'welcome';

  const isEnglish = i18n.language.toLowerCase().startsWith('en');
  const starterCards = useMemo(() => {
    const source = cards.length > 0 ? cards : defaultCards;
    const localized = source.flatMap((card) => {
      const rawText = isEnglish ? card.text_en?.trim() : card.text?.trim();
      if (!rawText) return [];
      return [
        {
          card,
          displayText: STARTER_COPY_CORRECTIONS[rawText] || rawText
        }
      ];
    });

    if (isEnglish && localized.length < 4) {
      for (const card of defaultCards) {
        const displayText = card.text_en!;
        if (localized.some((item) => item.displayText === displayText))
          continue;
        localized.push({ card, displayText });
        if (localized.length === 4) break;
      }
    }

    return localized.slice(0, 4);
  }, [cards, isEnglish]);

  const [greeting, setGreeting] = useState('');

  useEffect(() => {
    const isSerbian = i18n.language.toLowerCase().startsWith('sr');

    const srMorning = [
      'Dobro jutro.',
      'Dobrodošli nazad.',
      'Kako mogu da vam pomognem?'
    ];
    const srDaytime = [
      'Dobar dan.',
      'Dobrodošli nazad.',
      'Možemo da nastavimo.'
    ];
    const srEvening = [
      'Dobro veče.',
      'Dobrodošli nazad.',
      'Kako mogu da vam pomognem?'
    ];
    const srBuckets: Record<GreetingBucket, string[]> = {
      morning: srMorning,
      daytime: srDaytime,
      evening: srEvening
    };

    const enMorning = [
      'Good morning.',
      'Welcome back.',
      'How can I help?'
    ];
    const enDaytime = [
      'Good afternoon.',
      'Welcome back.',
      'Ready to continue?'
    ];
    const enEvening = [
      'Good evening.',
      'Welcome back.',
      'How can I help?'
    ];
    const enBuckets: Record<GreetingBucket, string[]> = {
      morning: enMorning,
      daytime: enDaytime,
      evening: enEvening
    };

    const buckets = isSerbian ? srBuckets : enBuckets;
    const selection = getGreetingSelection(window.localStorage);
    setGreeting(buckets[selection.bucket][selection.choiceIndex]);
  }, [i18n.language]);

  const onPaste = useCallback((event: ClipboardEvent) => {
    if (event.clipboardData && event.clipboardData.items) {
      const items = Array.from(event.clipboardData.items);
      items.forEach((item) => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            onFileUpload([file]);
          }
        }
      });
    }
  }, []);

  const onSubmit = useCallback(
    async (msg: string, _attachments?: IAttachment[], commandId?: string) => {
      const message: IStep = {
        threadId: '',
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: appendGitHubRepositoryReference(msg, selectedRepoUrl),
        createdAt: new Date().toISOString(),
        metadata: { location: window.location.href }
      };

      const fileReferences = attachments
        .filter((a) => !!a.serverId)
        .map((a) => ({ id: a.serverId! }));

      if (commandId) {
        message.command = commandId;
      }

      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
      sendMessage(message, fileReferences);
    },
    [user, attachments, selectedRepoUrl, sendMessage, autoScrollRef]
  );

  const onReply = useCallback(
    async (msg: string) => {
      const message: IStep = {
        threadId: '',
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: msg,
        createdAt: new Date().toISOString(),
        metadata: { location: window.location.href }
      };

      replyMessage(message);
      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
    },
    [user, replyMessage, autoScrollRef]
  );

  const canSubmit =
    !disabled &&
    (askUser ? !!value.trim() : !!value.trim() || !!selectedRepoUrl);

  const submit = useCallback(() => {
    if (!canSubmit) return;

    if (askUser) {
      onReply(value);
    } else {
      onSubmit(value, attachments, selectedCommand?.id);
    }
    attachments.forEach((attachment) => {
      if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
    });
    setAttachments([]);
    setSelectedRepoUrl(null);
    inputRef.current?.reset();
    setValue('');
  }, [
    value,
    canSubmit,
    askUser,
    attachments,
    selectedCommand,
    setAttachments,
    selectedRepoUrl,
    onSubmit,
    onReply
  ]);

  const handleRecommendationClick = useCallback(
    (text: string) => {
      if (disabled) return;
      onSubmit(text, [], undefined);
    },
    [disabled, onSubmit]
  );

  const SelectedCommandChip = () => {
    if (!selectedCommand) return null;
    const iconName =
      COMMAND_ICON_MAP[selectedCommand.icon || ''] ||
      selectedCommand.icon ||
      'star';

    return (
      <button
        onClick={() => setSelectedCommand(undefined)}
        className="fongpt-hover group flex items-center justify-center gap-2 pl-2.5 pr-1.5 py-1.5 md:py-2 rounded-full bg-primary/10 border border-outline text-sm font-medium text-primary"
      >
        <MaterialIcon name={iconName} className="text-lg" />
        <span className="max-w-[120px] md:max-w-[160px] truncate">
          {selectedCommand.id}
        </span>
        <span className="w-5 h-5 flex items-center justify-center">
          <MaterialIcon
            name="close"
            className="text-[14px] transition-all duration-300 opacity-60 group-hover:opacity-100 scale-90 group-hover:scale-100"
          />
        </span>
      </button>
    );
  };

  const SelectedRepositoryChip = () => {
    if (!selectedRepoUrl) return null;

    const removeLabel = isEnglish
      ? 'Remove GitHub repository'
      : 'Ukloni GitHub repozitorijum';

    return (
      <div
        aria-label={isEnglish ? 'GitHub repository' : 'GitHub repozitorijum'}
        className="group relative ml-2 grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-outline/80 bg-black/10 text-primary"
      >
        <GitHub className="!size-5 fill-current" />
        <button
          aria-label={removeLabel}
          className="absolute -right-1.5 -top-1.5 grid h-4 w-4 place-items-center rounded-full bg-primary text-[10px] text-background opacity-0 shadow-sm transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
          onClick={() => setSelectedRepoUrl(null)}
          title={removeLabel}
          type="button"
        >
          ×
        </button>
      </div>
    );
  };

  const composerBody = (
    <div className="fongpt-composer relative flex flex-col gap-1.5 rounded-[28px] px-3.5 pb-2 pt-3 transition-all sm:px-4 sm:pb-2.5 sm:pt-3.5">
      {attachments.length > 0 ? (
        <div className="w-full pt-1">
          <Attachments />
        </div>
      ) : null}

      <SelectedRepositoryChip />

      <Input
        ref={inputRef}
        id="chat-input"
        selectedCommand={selectedCommand}
        setSelectedCommand={setSelectedCommand}
        onChange={setValue}
        onPaste={onPaste}
        onEnter={submit}
        placeholder={
          isWelcome
            ? t('chat.input.placeholder', 'How can I help you today?')
            : t('chat.input.placeholder', 'Send a Message')
        }
        className="min-h-8 max-h-40 resize-none border-none bg-transparent px-2 text-base text-primary outline-none placeholder:text-primary/35 focus:ring-0 empty:before:text-primary/35"
      />
      <div className="flex items-center justify-between gap-2 w-full">
        <div className="flex items-center gap-1 min-w-0">
          <AttachmentMenu
            disabled={disabled}
            fileSpec={fileSpec}
            selectedCommandId={selectedCommand?.id}
            onFileUpload={onFileUpload}
            onFileUploadError={onFileUploadError}
            onRepoSelected={onRepoSelected}
            onRepoError={onRepoError}
            onCommandSelect={setSelectedCommand}
          />
          <SelectedCommandChip />
        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0">
          <ChatProfiles navigate={navigate} compact />
          <VoiceButton disabled={disabled} />
          <SubmitButton onSubmit={submit} disabled={!canSubmit} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {isWelcome ? (
        <div className="fongpt-welcome relative z-[2] flex flex-col items-center w-full">
          <div className="fongpt-welcome-hero mb-4 w-full sm:mb-6 md:mb-8">
            <h1>{greeting}</h1>
          </div>

          <div className="relative z-[2] w-full max-w-[48rem]">
            {composerBody}
          </div>

          <div className="fongpt-starters relative z-[2] mt-3 flex w-full max-w-[48rem] flex-wrap justify-center gap-2 sm:mt-4">
            {starterCards.map(({ card, displayText }) => {
              return (
                <button
                  key={`${card.id}-${displayText}`}
                  onClick={() => handleRecommendationClick(displayText)}
                  disabled={disabled}
                  className="starter-prompt flex cursor-pointer items-center gap-2 rounded-full px-3 py-1.5 text-left disabled:opacity-50 sm:px-3.5 sm:py-2"
                >
                  {/* The icon and label inherit the chip's colour so the whole
                      chip lights up as one on hover. */}
                  <MaterialIcon
                    name={getMaterialIcon(card.icon)}
                    className="text-[15px] sm:text-base"
                  />
                  <span className="text-[12px] font-medium sm:text-[13px]">
                    {displayText}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="relative w-full">{composerBody}</div>
      )}
    </div>
  );
}
